import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { DataTableExpandedRows } from 'primereact/datatable';
import { chain, sumBy } from 'lodash';
import { useReactToPrint } from 'react-to-print';
import { Button } from 'primereact/button';
import dayjs from 'dayjs';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { TratamentoSementesFormType } from '../types';
import { ItemSaldoUnidade } from '../../../../@types';
import { calculaQuantidadeInsumo, isSegundoTratamento } from '../utils';
import ResumoInsumosTable from './components/insumos-table';
import GeneralInfoTable from './components/general-info-table';
import Printable from '../../printable';
import { DataToPrintTS } from '../../types';

type InsumoRow = {
  id: number | undefined;
  descricao: string | undefined;
  total: number;
  unidade: ItemSaldoUnidade;
};

const TratamentoDeSementesResumo: FC<TratamentoSementesFormType> = ({
  formObject,
  tratamentoSementes,
}) => {
  const tratamentos = formObject.form.watch('tratamentos');
  const [atividade, propriedade, safra] = formObject.form.watch([
    'atividade',
    'propriedade',
    'safra',
  ]);
  const [dataToPrint, setDataToPrint] = useState<DataToPrintTS>({});

  const printRef = useRef(null);
  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => setDataToPrint({}),
  });

  const expandedRows: DataTableExpandedRows = useMemo(
    () =>
      tratamentos.reduce<DataTableExpandedRows>((acc, tratamento) => {
        if (tratamento.identification) {
          acc[tratamento.identification] = true;
        }
        return acc;
      }, {}),
    [tratamentos],
  );

  const onPressPrint = useImmutableCallback(() => {
    setDataToPrint({
      tratamentosParsed: tratamentos,
      atividade,
      propriedade,
      safra,
      status: tratamentoSementes?.status || 'aberto',
      dataHoraImpressao: dayjs().format('DD/MM/YYYY HH:mm'),
    });
  });

  const insumos: InsumoRow[] = useMemo(
    () =>
      chain(tratamentos)
        .map((tratamento) =>
          tratamento.defensivos.map((item) => ({
            ...item,
            tratamentoPeso: tratamento.peso,
            tratamentoDose: item.dose,
            tratamentoQuantidade: tratamento.quantidade,
            segundoTratamento: isSegundoTratamento(tratamento),
          })),
        )
        .flatten()
        .groupBy('item.id')
        .map<InsumoRow>((defensivos) => {
          const defensivosComTotal = defensivos.map((item) => {
            const total = item.segundoTratamento
              ? item.quantidade || 0
              : calculaQuantidadeInsumo({
                  dose: item.tratamentoDose || 0,
                  peso: item.tratamentoPeso || 0,
                  quantidade: item.tratamentoQuantidade || 0,
                });

            return {
              ...item,
              total,
            };
          });

          return {
            id: defensivos[0].item?.id,
            descricao: defensivos[0].item?.descricao,
            total: sumBy(defensivosComTotal, 'total'),
            unidade: defensivos[0].item?.unidade,
          };
        })
        .value(),
    [tratamentos],
  );

  useEffect(() => {
    if (!dataToPrint.tratamentosParsed?.length) return;

    triggerPrint();
  }, [triggerPrint, dataToPrint]);

  return (
    <div>
      <div className="print-container">
        <Button
          icon="pi pi-print"
          className="print-button button-primary"
          onClick={onPressPrint}
        />
      </div>
      <h2 className="pl-2">Tratamento de sementes</h2>
      <GeneralInfoTable tratamentos={tratamentos} expandedRows={expandedRows} />
      <ResumoInsumosTable insumos={insumos} />
      <Printable data={dataToPrint} insumos={insumos} ref={printRef} />
    </div>
  );
};

export default TratamentoDeSementesResumo;
